import type { InAppHref, NavigatePort } from '@heidi/runtime/shell';
import { useEffect, useRef } from 'react';

const ONBOARDING_ROUTE = '/scribe/new-user';
const GUARDED_ROUTE_PREFIX = '/scribe';

export interface UseResumePendingOnboardingOptions {
  /** Whether the in-app flow can resume at all; false leaves the user where they are. */
  canResume: boolean;
  /** Whether `canResume` reflects a settled flag read; false while flags are still loading. */
  isResumeDecided: boolean;
  hasAuthToken: boolean;
  isUserLoaded: boolean;
  /** `onboarding_pending_at` from the current user. */
  onboardingPendingAt: string | null | undefined;
  /** Onboarding was finished in this tab, even if saving it failed. */
  hasCompletedOnboarding: boolean;
  pathname: string;
  /** Whether the current URL carries `from=shared-template`. */
  isFromSharedTemplate: boolean;
  isDesktopOffline: boolean;
  /** Adds the query params that must survive an in-app redirect. */
  preserveRedirectParams: (href: InAppHref) => InAppHref;
  onNavigate: NavigatePort;
  /** Fired once when a pending user cannot resume, so the caller can clear the mark. */
  onCannotResume: () => void;
}

/**
 * Sends a user the /auth/onboard page created, and who has not finished
 * the in-app steps, from anywhere in `/scribe` back to onboarding.
 */
export function useResumePendingOnboarding({
  canResume,
  isResumeDecided,
  hasAuthToken,
  isUserLoaded,
  onboardingPendingAt,
  hasCompletedOnboarding,
  pathname,
  isFromSharedTemplate,
  isDesktopOffline,
  preserveRedirectParams,
  onNavigate,
  onCannotResume,
}: UseResumePendingOnboardingOptions): void {
  const isOnboardingPending = !!onboardingPendingAt;
  const hasRedirected = useRef(false);
  const hasFiredCannotResume = useRef(false);

  useEffect(() => {
    if (hasRedirected.current) return;
    if (
      !canResume ||
      !isResumeDecided ||
      !hasAuthToken ||
      !isUserLoaded ||
      !isOnboardingPending
    ) {
      return;
    }
    if (hasCompletedOnboarding || isDesktopOffline) return;
    if (!pathname.startsWith(GUARDED_ROUTE_PREFIX) || pathname === ONBOARDING_ROUTE) {
      return;
    }

    hasRedirected.current = true;
    const target = preserveRedirectParams(
      isFromSharedTemplate ? `${ONBOARDING_ROUTE}?from=shared-template` : ONBOARDING_ROUTE
    );
    // A cancelled navigation must not spend the latch.
    Promise.resolve(onNavigate(target)).catch(() => {
      hasRedirected.current = false;
    });
  }, [
    canResume,
    isResumeDecided,
    hasAuthToken,
    isUserLoaded,
    isOnboardingPending,
    hasCompletedOnboarding,
    isDesktopOffline,
    pathname,
    isFromSharedTemplate,
    preserveRedirectParams,
    onNavigate,
  ]);

  // The mark is server state; left set, a later flag ramp would pull the user back in.
  useEffect(() => {
    if (hasFiredCannotResume.current) return;
    if (
      !hasAuthToken ||
      !isUserLoaded ||
      !isOnboardingPending ||
      !isResumeDecided ||
      canResume ||
      hasCompletedOnboarding ||
      isDesktopOffline
    ) {
      return;
    }

    hasFiredCannotResume.current = true;
    onCannotResume();
  }, [
    hasAuthToken,
    isUserLoaded,
    isOnboardingPending,
    isResumeDecided,
    canResume,
    hasCompletedOnboarding,
    isDesktopOffline,
    onCannotResume,
  ]);
}
